/* eslint-disable import/no-webpack-loader-syntax */
import { useEffect, useRef } from "react";
import { render } from "react-dom";
import mapboxgl from "!mapbox-gl";
import { useTranslation } from "react-i18next";
import { isDefined } from "../../Utils";

const Popup = ({ map, activeFeature, onClose }) => {
  const { t } = useTranslation("common");
  const popupRef = useRef();
  const contentRef = useRef(document.createElement("div"));

  // App customers (mobile-app prospects/clients) get their own field list.
  const appCustomerRows = [
    { label: t("Last name"), value: activeFeature?.lastName },
    { label: t("First name"), value: activeFeature?.firstName },
    { label: t("Email"), value: activeFeature?.email },
    { label: t("Phone number"), value: activeFeature?.phoneNumber },
  ];

  // Competitors: limited info (PPTX — partners never see the full record).
  const competitorRows = [
    { label: t("Company name"), value: activeFeature?.name },
    { label: t("Phone"), value: activeFeature?.phoneNumber },
    { label: t("Email"), value: activeFeature?.email },
  ];

  const defaultRows = [
    {
      label: activeFeature?.isProspect ? t("Prospect") : t("Store"),
      value: activeFeature?.name,
    },
    {
      label: t("Potential Spa/year"),
      value: activeFeature?.annualSpaPotential ?? "0",
      hide: !activeFeature?.isProspect,
    },
    {
      label: t("Objectif Spa/Contract"),
      value: activeFeature?.contract?.data?.attributes?.objectif ?? "0",
      hide: activeFeature?.isProspect,
    },
    {
      label: `${t("Sales Year")} ${new Date().getFullYear()}`,
      value: activeFeature?.soldSpa ?? "0",
      hide: activeFeature?.isProspect,
    },
    {
      label: t("Note"),
      value: isDefined(activeFeature?.note?.data?.attributes?.globalNote)
        ? `${activeFeature?.note?.data?.attributes?.globalNote}/20`
        : "",
    },
    {
      label: t("Website Link"),
      value: activeFeature?.website,
      type: "URL",
    },
    {
      label: t("Email"),
      value: activeFeature?.email,
    },
    {
      label: t("Phone"),
      value: activeFeature?.phoneNumber,
    },
  ];

  // ERP prospects shown as competitors (isCompetitor + isProspect) keep the
  // partners-map prospect field list; only competitor records get the short
  // company/phone/email list.
  const rows = activeFeature?.isAppCustomer
    ? appCustomerRows
    : activeFeature?.isCompetitor && !activeFeature?.isProspect
    ? competitorRows
    : defaultRows;

  const getClickableLink = (link) => {
    return link.startsWith("https://") ? link : `https://${link}`;
  };

  useEffect(() => {
    if (!map) return;

    popupRef.current = new mapboxgl.Popup({
      closeOnClick: false,
      closeButton: false,
      offset: 20,
    });

    render(
      <div className="portal-content f16 quickSand">
        <button
          className="position-absolute top-0 right-0 p-1 px-2 border-0 bg-transparent z-9999 f20 outline-none"
          aria-label="Close"
          onClick={() => {
            onClose();
            popupRef.current.remove();
          }}
        >
          x
        </button>
        <table>
          <tbody className="text-nowrap">
            {rows.map(({ label, value, hide, type }) => {
              if (!value || hide) return null;
              return (
                <tr key={label}>
                  {type !== "URL" && (
                    <td className="py-1 ps-3 pe-1">{label}</td>
                  )}
                  <td className="py-1 ps-3 pe-1">
                    {type === "URL" ? (
                      <a
                        href={getClickableLink(value)}
                        target="_blank"
                        rel="noreferrer"
                        className="underline text-dark"
                      >
                        {label}
                      </a>
                    ) : (
                      <strong>{value}</strong>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {activeFeature?.onSeeMore ? (
          <div className="text-center py-1">
            <button
              className="border-0 bg-transparent underline f14"
              onClick={() => {
                onClose();
                popupRef.current.remove();
                activeFeature.onSeeMore();
              }}
            >
              {t("See more")}
            </button>
          </div>
        ) : null}
      </div>,
      contentRef.current
    );

    return () => {
      popupRef.current.remove();
    };
  }, [map, activeFeature]);

  useEffect(() => {
    if (!activeFeature) {
      popupRef?.current?.remove();
      return;
    }

    popupRef.current
      .setLngLat(activeFeature.markers[activeFeature.markerIndex].coordinates)
      .setDOMContent(contentRef.current)
      .addTo(map);
  }, [activeFeature]);

  return null;
};

export default Popup;
